// src/api/adminApi.js
// Admin API Service for handling admin endpoints

import axiosInstance, { getErrorMessage } from './axiosInstance';
import API_CONFIG from '../config/api';

// Admin API Service
export const adminApi = {
  // Get all clients
  getClients: async (params = {}) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_CLIENTS, { params });
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب قائمة العملاء');
    }
  },

  // Get client detail
  getClientDetail: async (clientId) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_CLIENT_DETAIL(clientId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب تفاصيل العميل');
    }
  },

  // Get client stats
  getClientStats: async (clientId) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_CLIENT_STATS(clientId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب إحصائيات العميل');
    }
  },

  // Update client
  updateClient: async (clientId, data) => {
    try {
      const response = await axiosInstance.put(API_CONFIG.ENDPOINTS.ADMIN_CLIENT_UPDATE(clientId), data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في تحديث العميل');
    }
  },

  testOurSmsConnection: async (clientId, data) => {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.ADMIN_CLIENT_TEST_OURSMS(clientId), data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'Failed to test OurSMS credentials');
    }
  },

  // Delete client
  deleteClient: async (clientId) => {
    try {
      const response = await axiosInstance.delete(API_CONFIG.ENDPOINTS.ADMIN_CLIENT_DELETE(clientId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في حذف العميل');
    }
  },

  // Create new client
  createClient: async (data) => {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.ADMIN_CLIENTS, data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في إنشاء العميل');
    }
  },

  // Get admin stats
  getAdminStats: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_STATS);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب إحصائيات الإدارة');
    }
  },

  // Get client growth chart data
  getClientGrowthChart: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_CLIENT_GROWTH_CHART);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب بيانات نمو العملاء');
    }
  },

  // Get token consumption chart data
  getTokenConsumptionChart: async (clientId = 'all') => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_TOKEN_CONSUMPTION_CHART, {
        params: clientId && clientId !== 'all' ? { client_id: clientId } : {},
      });
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب بيانات استهلاك التوكنز');
    }
  },

  // Get system logs
  getSystemLogs: async (params = {}) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_LOGS, { params });
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب سجلات النظام');
    }
  },

  // ===== Department Management API =====
  
  // Get all departments
  getDepartments: async (params = {}) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENTS, { params });
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب قائمة الأقسام');
    }
  },

  // Get department detail
  getDepartmentDetail: async (departmentId) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_DETAIL(departmentId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب تفاصيل القسم');
    }
  },

  // Create new department
  createDepartment: async (data) => {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_CREATE, data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في إنشاء القسم');
    }
  },

  // Update department
  updateDepartment: async (departmentId, data) => {
    try {
      const response = await axiosInstance.put(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_UPDATE(departmentId), data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في تحديث القسم');
    }
  },

  // Delete department
  deleteDepartment: async (departmentId) => {
    try {
      const response = await axiosInstance.delete(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_DELETE(departmentId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في حذف القسم');
    }
  },

  // ===== Department Phone Numbers API =====

  // Get department phone numbers
  getDepartmentPhones: async (departmentId) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_PHONES(departmentId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب أرقام الهاتف');
    }
  },

  // Create department phone number
  createDepartmentPhone: async (departmentId, data) => {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_PHONE_CREATE(departmentId), data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في إنشاء رقم الهاتف');
    }
  },

  // Update department phone number
  updateDepartmentPhone: async (departmentId, phoneId, data) => {
    try {
      const response = await axiosInstance.put(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_PHONE_UPDATE(departmentId, phoneId), data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في تحديث رقم الهاتف');
    }
  },

  // Delete department phone number
  deleteDepartmentPhone: async (departmentId, phoneId) => {
    try {
      const response = await axiosInstance.delete(API_CONFIG.ENDPOINTS.ADMIN_DEPARTMENT_PHONE_DELETE(departmentId, phoneId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في حذف رقم الهاتف');
    }
  },

  // ===== Transfer System Stats =====

  // Get transfer statistics
  getTransferStats: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_TRANSFER_STATS);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب إحصائيات التحويل');
    }
  },

  // Get transfer logs
  getTransferLogs: async (params = {}) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.ADMIN_TRANSFER_LOGS, { params });
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب سجلات التحويل');
    }
  }
};

// Client-specific API calls
export const clientApi = {
  // Get client stats
  getClientStats: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.CLIENT_STATS);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب إحصائيات العميل');
    }
  },

  // Get client conversations
  getClientConversations: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.CLIENT_CONVERSATIONS);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب المحادثات');
    }
  },

  // Get conversation detail
  getConversationDetail: async (conversationId) => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.CLIENT_CONVERSATION_DETAIL(conversationId));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب تفاصيل المحادثة');
    }
  },

  // Get knowledge base
  getKnowledgeBase: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.CLIENT_KNOWLEDGE_BASE);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب قاعدة المعرفة');
    }
  },

  // Update knowledge base
  updateKnowledgeBase: async (data) => {
    try {
      const response = await axiosInstance.put(API_CONFIG.ENDPOINTS.CLIENT_KNOWLEDGE_BASE, data);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في تحديث قاعدة المعرفة');
    }
  },

  // Get token chart data
  getTokenChart: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.CLIENT_TOKEN_CHART);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب بيانات التوكنز');
    }
  },

  // Get conversation chart data
  getConversationChart: async () => {
    try {
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.CLIENT_CONVERSATION_CHART);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      throw new Error(typeof errorMessage === 'string' ? errorMessage : 'فشل في جلب بيانات المحادثات');
    }
  },
};

export default adminApi;
